/**
 * Instrument search datasets and the data-vendor flags that gate them.
 *
 * Single source of truth for "which datasets exist" and "which of them may this
 * tenant use", shared by the search service (which refuses a disabled dataset),
 * the designer AI tool (whose `dataset` enum is narrowed to the enabled ones
 * before the model ever sees it) and anything else that offers a dataset choice.
 */

import { FEATURE_FLAGS, type FeatureFlagKey, isFeatureEnabled } from "./feature-flags";

/** Every dataset instrument search accepts. */
export const INSTRUMENT_DATASETS = [
  "morningstar_funds",
  "morningstar_etf",
  "morningstar_category",
  "morningstar_index",
  "eodhd",
  "twelve",
  "combined",
  "custom",
] as const;

export type InstrumentDataset = (typeof INSTRUMENT_DATASETS)[number];

/**
 * External datasets gated by a per-tenant data-vendor flag, with the label used
 * in the "not enabled for your organization" error. A dataset absent from this
 * map is always available — `custom` and `combined` are the tenant's own data.
 */
export const DATASET_VENDOR_FLAG: Partial<
  Record<InstrumentDataset, { flag: FeatureFlagKey; label: string }>
> = {
  morningstar_funds: { flag: FEATURE_FLAGS.MORNINGSTAR_DATA, label: "Morningstar data" },
  morningstar_etf: { flag: FEATURE_FLAGS.MORNINGSTAR_DATA, label: "Morningstar data" },
  morningstar_category: { flag: FEATURE_FLAGS.MORNINGSTAR_DATA, label: "Morningstar data" },
  morningstar_index: { flag: FEATURE_FLAGS.MORNINGSTAR_INDEX, label: "Morningstar Indexes" },
  twelve: { flag: FEATURE_FLAGS.TWELVE_DATA, label: "Twelve Data" },
  eodhd: { flag: FEATURE_FLAGS.EODHD, label: "EODHD" },
};

/**
 * The datasets a tenant may use, given its feature-flag rows (as returned by
 * `getFlagsForTenant`). Pure: the caller does the one query.
 */
export function enabledDatasetsFrom(
  flags: { flagKey: string; enabled: boolean }[],
): InstrumentDataset[] {
  return INSTRUMENT_DATASETS.filter((dataset) => {
    const vendor = DATASET_VENDOR_FLAG[dataset];
    return !vendor || isFeatureEnabled(flags, vendor.flag);
  });
}
